import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Quote, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { GoogleG } from "@/components/site/Reviews";

const useTrust = () => {
  const { projects } = useSite();
  const [reviews, setReviews] = useState([]);
  const [google, setGoogle] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {});
    api.get("/reviews/google").then((r) => setGoogle(r.data)).catch(() => {});
  }, []);
  return { clients: projects.filter((p) => !p.is_concept), reviews, google };
};

export const TrustLine = () => {
  const { clients, google } = useTrust();
  if (!clients.length && !google) return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="advisor-trust-line">
      {google && (
        <span className="flex items-center gap-2 text-sm">
          <GoogleG className="w-5 h-5" />
          <span className="font-semibold">{google.rating.toFixed(1)}</span>
          <span className="flex text-[#D6AD62]">{Array.from({ length: 5 }, (_, i) => <Star key={i} size={13} fill="currentColor" />)}</span>
          <span className="text-[#a9a398] text-xs">{google.count} Google rəyi</span>
        </span>
      )}
      {clients.length > 0 && (
        <span className="flex items-center gap-3">
          <span className="flex -space-x-2">
            {clients.slice(0, 5).map((p) => (
              <span key={p.id} className="h-8 w-8 rounded-full overflow-hidden border-2 border-[#111110] bg-white grid place-items-center">
                {p.client_logo ? <img src={p.client_logo} alt={p.title} className="h-full w-full object-cover" /> : <span className="text-[10px] font-bold text-[#171715]">{p.title[0]}</span>}
              </span>
            ))}
          </span>
          <span className="text-xs text-[#a9a398]"><span className="text-[#F3EEE4] font-semibold">{clients.length}+ biznes</span> artıq bizimlə işləyir</span>
        </span>
      )}
    </div>
  );
};

export const TrustSection = () => {
  const { clients, reviews } = useTrust();
  const top = reviews.slice(0, 3);
  if (!top.length && !clients.length) return null;
  return (
    <section className="container-lm pt-16" data-testid="advisor-trust-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow mb-3">Real nəticələr</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">Bizə etibar edən bizneslər</h2>
        </div>
        <Link to="/layiheler" className="text-sm font-semibold text-[#b58a3f] flex items-center gap-1 hover:gap-2 transition-all" data-testid="advisor-trust-all-projects">Bütün layihələr <ArrowRight size={15} /></Link>
      </div>

      {clients.length > 0 && (
        <div className="flex gap-3 overflow-x-auto pb-2 mt-6 -mx-1 px-1">
          {clients.slice(0, 8).map((p) => (
            <Link key={p.id} to={`/layihe/${p.slug}`} className="shrink-0 w-[160px] h-[60px] rounded-xl overflow-hidden border border-[#171715]/[0.07] bg-white hover:-translate-y-0.5 transition-transform" data-testid={`advisor-client-${p.slug}`}>
              {p.client_logo ? <img src={p.client_logo} alt={p.title} className="w-full h-full object-cover" loading="lazy" /> : <span className="w-full h-full grid place-items-center font-display font-semibold text-[#171715]/70 px-2 text-center">{p.title}</span>}
            </Link>
          ))}
        </div>
      )}

      {top.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          {top.map((r) => (
            <figure key={r.id} className="relative rounded-2xl bg-white/70 border border-[#171715]/5 p-6 flex flex-col" data-testid={`advisor-review-${r.id}`}>
              <Quote size={24} className="absolute top-6 right-6 text-[#D6AD62]/40" />
              <div className="flex text-[#b58a3f] gap-0.5">{Array.from({ length: r.rating }, (_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
              <blockquote className="text-[15px] leading-relaxed mt-3 flex-1 line-clamp-5">«{r.text}»</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                {r.avatar && <img src={r.avatar} alt="" className="w-10 h-10 rounded-lg object-contain bg-white border border-[#171715]/[0.07] p-0.5" />}
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-tight">{r.company || r.name}</p>
                  {r.company && <p className="text-xs text-[#77736B]">{r.name}</p>}
                </div>
                {r.project_slug && <Link to={`/layihe/${r.project_slug}`} className="ml-auto text-xs underline underline-offset-4 text-[#77736B] hover:text-[#171715] shrink-0">Layihə</Link>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
};
